import { Button } from "@/components/ui/button";
import { SheetActions, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { cn, receiptInputData } from "@/utils/utils";
import { IconBox } from "@common/components";
import { useTestExpensePolicyMutation } from "@common/utils/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { Body, Heading, Superscript } from "@hypercard/web-components";
import { CheckCircle, SpinnerGap, Warning } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import FeedbackDialog from "@common/components/FeedbackDialog";
import { z } from "zod";
import { useRecoilValue } from "recoil";
import { createOrEditStipendState } from "@common/state/createOrEditStipendState";
import { formatDollars } from "@common/components/utils";
import { CreateStipendForm } from "@domains/stipends/pages/sheets/CreateStipendForm";
import { StipendFormValues } from "@domains/stipends/pages/forms/Forms";

type Props = {
  onClose: () => void;
  terms: string;
};

type TestStipendRuleState = "form" | "success" | "error" | "loading";
export const TestStipendRule = ({ terms, onClose }: Props) => {
  const {
    amount: maxAmount,
    name,
    longDetails,
    category,
  } = useRecoilValue(createOrEditStipendState);
  const { toast } = useToast();
  const [state, setState] = useState<TestStipendRuleState>("form");
  const [reason, setReason] = useState<string | null>(null);

  const formSchema = z.object({
    merchant: z.string().min(1, { message: "Merchant is required" }),
    amount: z.coerce
      .number({ message: "Amount is required" })
      .min(0.01, { message: "Amount must be greater than $0.00" })
      .max(maxAmount!, {
        message: `Amount cannot exceed ${formatDollars(maxAmount)}`,
      }),
    date: z.date({
      required_error: "A transaction date is required",
    }),
    receipt: z.any().default(null),
    notes: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const [testExpensePolicy] = useTestExpensePolicyMutation();

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    const { merchant, amount, date, notes, receipt } = values;
    const [, receiptFileData] = await receiptInputData(receipt);
    setState("loading");
    const policy = `\n\nPolicy Name:${name} \n\nCategory: ${category} \n\n${longDetails} \n${terms}`;
    try {
      const { data } = await testExpensePolicy({
        variables: {
          input: {
            policy,
            expense: {
              merchantName: merchant,
              amount,
              transactionDate: format(date, "yyyy-MM-dd"),
              notes,
              receiptFileData,
            },
          },
        },
      });
      if (
        data?.testExpensePolicy.__typename ===
        "MutationTestExpensePolicySuccess"
      ) {
        setReason(data.testExpensePolicy?.data?.reason ?? null);
        setState(
          data.testExpensePolicy.data.isAutoApproved ? "success" : "error",
        );
      } else {
        throw new Error();
      }
    } catch (error) {
      setState("form");
      toast({
        variant: "destructive",
        title:
          "An error occured while testing the stipend rules. Please try again.",
      });
    }
  };

  const handlePrefillForm = (formValues: Partial<StipendFormValues>) => {
    const amount =
      formValues.amount && formValues.amount > maxAmount!
        ? maxAmount
        : formValues.amount;
    form.reset({ ...formValues, amount });
  };

  const handleSubmitAnother = () => {
    if (state === "error") {
      form.reset(form.getValues());
    } else {
      form.reset({
        merchant: undefined,
        amount: undefined,
        date: undefined,
        notes: undefined,
        receipt: undefined,
      });
    }
    setReason(null);
    setState("form");
  };

  const handleClose = () => {
    onClose();
  };

  let header = "Auto-Approved";
  let body =
    "This expense would have been auto-approved with the stipend rules";
  let buttonText = "Test another expense";
  if (state === "error") {
    header = "Out of Policy";
    body =
      "This expense would have been pushed to manual review with the stipend rules";
    buttonText = "Try again";
  }

  return (
    <>
      {state === "form" && (
        <>
          <SheetHeader>
            <SheetTitle>Test Stipend Rules</SheetTitle>
          </SheetHeader>
          <div className="px-sheet-gap">
            <CreateStipendForm
              viewOnly={false}
              form={form}
              isSubmitting={false}
              onSubmit={handleSubmit}
              onParseReceipt={handlePrefillForm}
            />
          </div>
        </>
      )}
      {state === "loading" && (
        <div className="flex grow flex-col items-center justify-center pb-[5.5rem]">
          <SpinnerGap className="size-16 animate-spin" />
          <Heading size="lg" weight="regular" className="mt-6">
            Testing Stipend Rules...
          </Heading>
        </div>
      )}
      {(state === "success" || state === "error") && (
        <>
          <div className="flex grow flex-col items-center justify-center px-sheet-gap pb-[5.5rem]">
            <Superscript size="md">TEST REPORT</Superscript>
            <Heading size="lg" weight="regular" className="mt-4">
              {header}
            </Heading>
            <Body
              size="md"
              weight="regular"
              className="mt-2 text-center text-white/70"
            >
              {body}
            </Body>
            {reason && (
              <div
                className={cn(
                  "mt-6 flex w-full items-start rounded-md border p-4",
                  state === "success"
                    ? "border-[#54C186] bg-[#54C1861A]"
                    : "border-error bg-[#BE3E221A]",
                )}
              >
                <IconBox className="mr-3">
                  {state === "success" ? (
                    <CheckCircle className="size-6 text-[#54C186]" />
                  ) : (
                    <Warning className="size-6 text-error" />
                  )}
                </IconBox>
                <div className="flex h-full flex-col justify-center">
                  <Body size="sm" weight="medium">
                    {reason}
                  </Body>
                </div>
              </div>
            )}
          </div>
          <SheetActions className="flex flex-col items-stretch space-y-4">
            <FeedbackDialog prefix="[From Test Stipend Rules]" />
            <Button
              onClick={handleSubmitAnother}
              variant="secondary"
              className="w-full"
            >
              {buttonText}
            </Button>
            <Button onClick={handleClose} className="w-full">
              Go back to Stipend Rules
            </Button>
          </SheetActions>
        </>
      )}
    </>
  );
};
